import { z } from "zod";
import { classifyFetchError } from "../state/denialErrors";

export const GLEAN_INSTALLER_MAX_BYTES = 100 * 1024 * 1024;
export const gleanReleaseVersionSchema = z.string().max(64).regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/);
const releaseSchema = z.object({
  platform: z.literal("windows"), version: gleanReleaseVersionSchema,
  filename: z.literal("VelocityGleanSetup.exe"), sha256: z.string().regex(/^[a-f0-9]{64}$/),
  size_bytes: z.number().int().positive().max(GLEAN_INSTALLER_MAX_BYTES),
  published_at: z.string().datetime({ offset: true }), release_notes: z.string().max(2_000),
  download_url: z.string().max(512),
}).superRefine((release, context) => {
  if (release.download_url !== `/api/workspace/glean-handoff/releases/windows/${release.version}/${release.sha256}/download`) {
    context.addIssue({ code: "custom", message: "Invalid release download address" });
  }
});
const adminSchema = z.object({ configured: z.boolean(), release: releaseSchema.nullable() });
const latestSchema = z.object({ release: releaseSchema.nullable() });
export type GleanRelease = z.infer<typeof releaseSchema>;
export type GleanReleaseAdminState = z.infer<typeof adminSchema>;

const ADMIN_URL = "/api/workspace/mission-control/glean-releases/windows";

async function request(url: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(url, { credentials: "include", cache: "no-store", ...init });
  if (!response.ok) throw await classifyFetchError(response, "Glean Helper releases");
  return response.json();
}

export async function getGleanReleaseAdmin(signal?: AbortSignal): Promise<GleanReleaseAdminState> {
  return adminSchema.parse(await request(ADMIN_URL, { signal }));
}

export async function getLatestWindowsGleanRelease(signal?: AbortSignal): Promise<GleanRelease | null> {
  return latestSchema.parse(await request("/api/workspace/glean-handoff/releases/latest?platform=windows", { signal })).release;
}

export interface PublishGleanReleaseInput {
  file: File;
  version: string;
  release_notes: string;
  expected_current_version: string;
}

export function validateGleanReleaseInput(input: PublishGleanReleaseInput): string | null {
  if (!/\.exe$/i.test(input.file.name) || input.file.size === 0) return "Choose a nonempty Windows .exe installer.";
  if (input.file.size > GLEAN_INSTALLER_MAX_BYTES) return "The installer must be 100 MiB or smaller.";
  if (!gleanReleaseVersionSchema.safeParse(input.version).success) return "Enter a version in X.Y.Z format, such as 1.2.0, without leading zeroes.";
  if (input.release_notes.length > 2_000) return "Release notes must be 2,000 characters or fewer.";
  if (input.expected_current_version !== "" && !gleanReleaseVersionSchema.safeParse(input.expected_current_version).success) return "Refresh the current release before publishing.";
  return null;
}

export async function publishWindowsGleanRelease(input: PublishGleanReleaseInput, signal?: AbortSignal): Promise<GleanReleaseAdminState> {
  const issue = validateGleanReleaseInput(input);
  if (issue) throw new Error(issue);
  const body = new FormData();
  body.append("file", input.file, input.file.name);
  body.append("version", input.version);
  body.append("release_notes", input.release_notes);
  body.append("expected_current_version", input.expected_current_version);
  // The browser supplies the multipart boundary; never retry a publication automatically.
  const result = adminSchema.parse(await request(ADMIN_URL, { method: "POST", body, signal }));
  if (!result.configured || result.release?.version !== input.version) throw new Error("Release publication could not be confirmed");
  return result;
}
